import { ReplaySubject } from 'rxjs';
import axios, { AxiosError } from 'axios';
import { TimelineEvent, TimelineQuarter, RoadmapData } from '@/interfaces/timeline';
import { 
  generateQuartersFromEvents, 
  calculateDateRange,
  DEFAULT_TIMELINE_CONFIG 
} from '@/utils/timeline.utils';

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  backoffMultiplier: number;
}

interface ErrorDetails {
  type: 'network' | 'server' | 'data' | 'unknown';
  message: string;
  originalError: any;
  retryable: boolean;
}

class RoadmapService {
  private roadmapDataSubject = new ReplaySubject<RoadmapData>(1);
  private loadingSubject = new ReplaySubject<boolean>(1);
  private errorSubject = new ReplaySubject<ErrorDetails | null>(1);
  
  private roadmapData: RoadmapData | null = null;
  private isLoading = false;
  private error: ErrorDetails | null = null;
  private retryConfig: RetryConfig = {
    maxRetries: 3,
    retryDelay: 1000,
    backoffMultiplier: 2
  };

  constructor() {
    this.loadingSubject.next(false);
    this.errorSubject.next(null);
  }

  /**
   * Fetch roadmap data from Google Sheets via backend API with retry logic
   */
  async fetchRoadmapData(): Promise<RoadmapData> {
    this.setLoading(true);
    this.setError(null);

    try {
      const transformedData = await this.fetchWithRetry();
      
      this.roadmapData = transformedData;
      this.roadmapDataSubject.next(transformedData);
      
      return transformedData;
    } catch (error) {
      const errorDetails = this.createErrorDetails(error);
      this.setError(errorDetails);
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Fetch data with automatic retry logic
   */
  private async fetchWithRetry(): Promise<RoadmapData> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        const response = await axios.get('/api/roadmap', {
          timeout: 10000, // 10 second timeout
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        // Validate response data
        if (!response.data) {
          throw new Error('No data received from server');
        }
        
        // The API already returns structured data, just validate and transform dates
        const transformedData = this.validateAndTransformApiData(response.data);
        
        return transformedData;
      } catch (error) {
        lastError = error;
        
        const errorDetails = this.createErrorDetails(error);
        
        // Don't retry if error is not retryable or we've reached max attempts
        if (!errorDetails.retryable || attempt === this.retryConfig.maxRetries) {
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = this.retryConfig.retryDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt);
        
        console.warn(`Roadmap fetch attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  }

  /**
   * Validate and transform API response data
   */
  private validateAndTransformApiData(apiData: any): RoadmapData {
    if (!apiData || typeof apiData !== 'object') {
      throw new Error('Invalid API response format');
    }

    const { events, quarters, dateRange } = apiData;

    if (!events || !Array.isArray(events)) {
      throw new Error('No events data received from API');
    }

    // Transform events to ensure dates are proper Date objects
    const transformedEvents: TimelineEvent[] = events.map((event: any) => ({
      ...event,
      date: new Date(event.date)
    }));

    // Transform quarters to ensure dates are proper Date objects
    const transformedQuarters: TimelineQuarter[] = quarters?.map((quarter: any) => ({
      ...quarter,
      startDate: new Date(quarter.startDate),
      endDate: new Date(quarter.endDate),
      months: quarter.months?.map((month: any) => new Date(month)) || []
    })) || [];

    // Transform date range
    const transformedDateRange = {
      start: dateRange?.start ? new Date(dateRange.start) : new Date(),
      end: dateRange?.end ? new Date(dateRange.end) : new Date()
    };

    return {
      events: transformedEvents,
      quarters: transformedQuarters,
      dateRange: transformedDateRange
    };
  }

  /**
   * Transform raw Google Sheets data into structured roadmap data (legacy method)
   * Expected columns: date, title, short description, full description, notes, data
   */
  private transformSheetDataToRoadmap(rawData: any[][]): RoadmapData {
    if (!rawData || rawData.length === 0) {
      throw new Error('No roadmap data received from Google Sheets');
    }

    // Parse events from sheet rows
    const events: TimelineEvent[] = [];
    let eventIndex = 0;

    for (const row of rawData) {
      if (row.length < 4) continue; // Skip incomplete rows
      
      const [dateStr, title, shortDescription, fullDescription, notes = '', data = ''] = row;
      
      // Validate and parse date
      const date = this.parseDate(dateStr);
      if (!date) {
        console.warn(`Skipping row with invalid date: ${dateStr}`);
        continue;
      }

      // Validate required fields
      if (!title || !shortDescription) {
        console.warn(`Skipping row with missing required fields: ${JSON.stringify(row)}`);
        continue;
      }

      // Determine position (alternating above/below)
      const position: 'above' | 'below' = eventIndex % 2 === 0 ? 'above' : 'below';

      const event: TimelineEvent = {
        id: `event-${eventIndex}`,
        date,
        title: title.trim(),
        shortDescription: shortDescription.trim(),
        fullDescription: fullDescription ? fullDescription.trim() : shortDescription.trim(),
        notes: notes.trim(),
        data: this.parseEventData(data),
        position
      };

      events.push(event);
      eventIndex++;
    }

    // Sort events by date
    events.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Generate quarters and date range
    const quarters = this.generateQuarters(events);
    const dateRange = this.calculateDateRange(events);

    return {
      events,
      quarters,
      dateRange
    };
  }

  /**
   * Parse date string into Date object with validation
   */
  private parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;

    // Try multiple date formats
    const formats = [
      // ISO format: YYYY-MM-DD
      /^\d{4}-\d{2}-\d{2}$/,
      // US format: MM/DD/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,
      // European format: DD/MM/YYYY
      /^\d{1,2}\/\d{1,2}\/\d{4}$/
    ];

    let date: Date;

    // Try parsing as ISO date first
    if (formats[0].test(dateStr)) {
      date = new Date(dateStr);
    } else {
      // Try parsing as general date
      date = new Date(dateStr);
    }

    // Validate the parsed date
    if (isNaN(date.getTime())) {
      return null;
    }

    return date;
  }

  /**
   * Parse event data field (could be JSON or plain text)
   */
  private parseEventData(dataStr: string): any {
    if (!dataStr) return null;

    try {
      return JSON.parse(dataStr);
    } catch {
      // If not JSON, return as string
      return dataStr;
    }
  }

  /**
   * Generate quarter information from events
   */
  private generateQuarters(events: TimelineEvent[]): TimelineQuarter[] {
    return generateQuartersFromEvents(events, DEFAULT_TIMELINE_CONFIG);
  }

  /**
   * Calculate overall date range for the timeline
   */
  private calculateDateRange(events: TimelineEvent[]): { start: Date; end: Date } {
    return calculateDateRange(events);
  }

  /**
   * Create detailed error information for better error handling
   */
  private createErrorDetails(error: any): ErrorDetails {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      
      if (axiosError.code === 'ECONNABORTED' || axiosError.code === 'TIMEOUT') {
        return {
          type: 'network',
          message: 'Request timed out. The server is taking too long to respond.',
          originalError: error,
          retryable: true
        };
      }
      
      if (axiosError.code === 'NETWORK_ERROR' || !axiosError.response) {
        return {
          type: 'network',
          message: 'Network error. Please check your internet connection.',
          originalError: error,
          retryable: true
        };
      }
      
      if (status === 404) {
        return {
          type: 'server',
          message: 'Roadmap data not found. The Google Sheets document may not be accessible.',
          originalError: error,
          retryable: false
        };
      }
      
      if (status === 403 || status === 401) {
        return {
          type: 'server',
          message: 'Access denied. Please check permissions for the Google Sheets document.',
          originalError: error,
          retryable: false
        };
      }
      
      if (status && status >= 500) {
        return {
          type: 'server',
          message: 'Server error. The service is temporarily unavailable.',
          originalError: error,
          retryable: true
        };
      }
      
      if (status === 429) {
        return {
          type: 'server',
          message: 'Too many requests. Please wait a moment before trying again.',
          originalError: error,
          retryable: true
        };
      }
    }
    
    // Check for data validation errors
    if (error.message && error.message.includes('No roadmap data')) {
      return {
        type: 'data',
        message: 'The roadmap document appears to be empty or incorrectly formatted.',
        originalError: error,
        retryable: false
      };
    }
    
    return {
      type: 'unknown',
      message: error.message || 'An unexpected error occurred while loading the roadmap.',
      originalError: error,
      retryable: true
    };
  }

  /**
   * Set loading state and notify subscribers
   */
  private setLoading(loading: boolean): void {
    this.isLoading = loading;
    this.loadingSubject.next(loading);
  }

  /**
   * Set error state and notify subscribers
   */
  private setError(error: ErrorDetails | null): void {
    this.error = error;
    this.errorSubject.next(error);
  }

  // Public observable getters
  getRoadmapData() {
    return this.roadmapDataSubject.asObservable();
  }

  getLoadingState() {
    return this.loadingSubject.asObservable();
  }

  getErrorState() {
    return this.errorSubject.asObservable();
  }

  // Utility methods for components
  getCurrentData(): RoadmapData | null {
    return this.roadmapData;
  }

  isCurrentlyLoading(): boolean {
    return this.isLoading;
  }

  getCurrentError(): ErrorDetails | null {
    return this.error;
  }

  /**
   * Refresh roadmap data (useful for manual refresh)
   */
  async refresh(): Promise<void> {
    await this.fetchRoadmapData();
  }

  /**
   * Check if current error is retryable
   */
  isErrorRetryable(): boolean {
    return this.error?.retryable ?? false;
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyErrorMessage(): string {
    return this.error?.message ?? 'An unexpected error occurred';
  }

  /**
   * Update retry configuration
   */
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }
}

export const roadmapService = new RoadmapService();